import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { putItem, getItem, deleteItem } from '../lib/idb';
import { syncAll, loadLocal } from '../lib/sync';
import { supabase } from '../lib/supabaseClient';
import { getOnboardingProvinceHint, clearOnboardingProvinceHint } from '../pages/Onboarding';
import { convertSubmissionToInvoice } from '../lib/invoiceService';

const DataContext = createContext(null);

/**
 * Returns the real sequential submission number, or null if we can't reach
 * the server right now (offline, or a transient error). A null number means
 * the submission is a provisional draft — the definitive number is assigned
 * at sync time (see lib/sync.js), never guessed locally. This guarantees the
 * sequence has no gaps and can never collide with another submission.
 */
async function getNextSubmissionNumber(userId) {
  if (!userId || !navigator.onLine) return null;
  try {
    const { data, error } = await supabase.rpc('next_submission_number', { p_user_id: userId });
    if (error || !data) return null;
    return String(data);
  } catch (_) {
    return null;
  }
}

export function DataProvider({ children }) {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  // Effective owner id (the user_id under which data is stored).
  // For team members, this is the OWNER's id; for everyone else, their own id.
  const [effectiveOwnerId, setEffectiveOwnerId] = useState(null);
  // Team role: 'owner' (default) or 'member'. Members see the owner's data
  // in read-only mode for the company profile.
  const [teamRole, setTeamRole] = useState('owner');
  const [ownerCompany, setOwnerCompany] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState(null);

  // Resolve the effective owner id once per session (cached in localStorage
  // so offline boot still works correctly). Called whenever the user changes.
  useEffect(() => {
    if (!user) {
      setEffectiveOwnerId(null);
      setTeamRole('owner');
      setOwnerCompany(null);
      return;
    }
    const cacheKey = `soumissionpro:effective_owner:${user.id}`;
    const roleCacheKey = `soumissionpro:team_role:${user.id}`;
    const ownerInfoKey = `soumissionpro:owner_info:${user.id}`;
    // Default to the user's own id; refine if we discover they're a member.
    let initial = user.id;
    try {
      const cached = window.localStorage.getItem(cacheKey);
      if (cached) initial = cached;
      const cachedRole = window.localStorage.getItem(roleCacheKey);
      if (cachedRole === 'member' || cachedRole === 'owner') setTeamRole(cachedRole);
      const cachedOwnerInfo = window.localStorage.getItem(ownerInfoKey);
      if (cachedOwnerInfo) {
        const oi = JSON.parse(cachedOwnerInfo);
        setOwnerCompany(oi.company || null);
        setOwnerEmail(oi.email || null);
      }
    } catch {
      // ignore
    }
    setEffectiveOwnerId(initial);

    if (!navigator.onLine) return;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data?.session?.access_token;
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const info = await res.json();
        const owner = info?.effective_owner_id || user.id;
        const role = info?.role === 'member' ? 'member' : 'owner';
        const prevOwner = (() => {
          try { return window.localStorage.getItem(cacheKey); } catch { return null; }
        })();
        setEffectiveOwnerId(owner);
        setTeamRole(role);
        setOwnerCompany(info?.owner_company || null);
        setOwnerEmail(info?.owner_email || null);
        try {
          window.localStorage.setItem(cacheKey, owner);
          window.localStorage.setItem(roleCacheKey, role);
          window.localStorage.setItem(
            ownerInfoKey,
            JSON.stringify({ company: info?.owner_company || null, email: info?.owner_email || null })
          );
        } catch {
          // ignore
        }
        // If the effective_owner_id changed since last session (e.g., the user
        // just accepted a team invitation OR left a team), wipe local IDB so
        // they don't see stale data from their previous state.
        if (prevOwner && prevOwner !== owner) {
          try {
            const { clearAllData } = await import('../lib/idb');
            await clearAllData();
          } catch (e) {
            console.warn('[team] failed to clear stale IDB after owner change', e);
          }
        }
      } catch {
        // ignore — fallback to user.id
      }
    })();
  }, [user]);

  const refreshLocal = useCallback(async () => {
    if (!user) return;
    const id = effectiveOwnerId || user.id;
    const { submissions, clients, templates, invoices, profile } = await loadLocal(id);
    setSubmissions(submissions);
    setClients(clients);
    setTemplates(templates || []);
    setInvoices(invoices || []);
    setProfile(profile);
  }, [user, effectiveOwnerId]);

  const doSync = useCallback(async () => {
    if (!user || !navigator.onLine) return;
    setSyncing(true);
    const id = effectiveOwnerId || user.id;
    await syncAll(id);
    await refreshLocal();
    setSyncing(false);
  }, [user, effectiveOwnerId, refreshLocal]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  useEffect(() => {
    if (online && user) doSync();
  }, [online, user, doSync]);

  useEffect(() => {
    if (!user) {
      setSubmissions([]);
      setClients([]);
      setTemplates([]);
      setInvoices([]);
      setProfile(null);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      await refreshLocal();
      await doSync();
      setLoading(false);
    })();
  }, [user, refreshLocal, doSync]);

  // Apply the province picked in onboarding the first time a fresh user lands
  // logged-in without any cloud profile. We can't reference saveProfile here
  // (declared lower) so we do a minimal inline upsert.
  useEffect(() => {
    if (!user || loading) return;
    if (profile) return; // already has a profile
    const hint = getOnboardingProvinceHint();
    if (!hint) return;
    const now = new Date().toISOString();
    const seed = {
      user_id: user.id,
      email: user.email,
      language: 'fr',
      province: hint,
      default_hourly_rate: 65,
      default_margin: 15,
      updated_at: now,
      synced: false,
    };
    (async () => {
      try {
        await putItem('profiles', seed);
        setProfile(seed);
        // 🛡️ Mark this user as having confirmed their province via onboarding.
        // Prevents the ProvinceConfirmModal from re-asking on the dashboard.
        try {
          window.localStorage.setItem(`soumissionpro_province_confirmed_v1:${user.id}`, '1');
        } catch { /* ignore */ }
        if (navigator.onLine) {
          const { synced, ...payload } = seed;
          const { error } = await supabase.from('users_profiles').upsert(payload, { onConflict: 'user_id' });
          if (!error) {
            await putItem('profiles', { ...seed, synced: true });
            clearOnboardingProvinceHint();
          }
        }
      } catch (e) {
        console.warn('[onboarding] auto-province save failed:', e);
      }
    })();
  }, [user, loading, profile]);

  useEffect(() => {
    if (!user) return;
    const iv = setInterval(() => {
      if (navigator.onLine) doSync();
    }, 30000);
    return () => clearInterval(iv);
  }, [user, doSync]);

  const saveSubmission = async (data) => {
    const now = new Date().toISOString();
    const ownerId = effectiveOwnerId || user.id;
    const isNew = !data.id;
    let submissionNumber = data.submission_number;
    if (!submissionNumber) {
      submissionNumber = await getNextSubmissionNumber(ownerId);
    }
    const sub = {
      id: data.id || crypto.randomUUID(),
      user_id: ownerId,
      submission_number: submissionNumber,
      status: data.status || 'draft',
      client_name: data.client_name || '',
      client_address: data.client_address || '',
      client_phone: data.client_phone || '',
      client_email: data.client_email || '',
      work_type: data.work_type || '',
      province: data.province || profile?.province || 'QC',
      materials: data.materials || [],
      labour: data.labour || {},
      summary: data.summary || {},
      notes: data.notes || '',
      photos: data.photos || [],
      expenses: data.expenses || [],
      last_reminded_at: data.last_reminded_at || null,
      client_notified_at: data.client_notified_at || null,
      expires_at: data.expires_at || new Date(Date.now() + 30 * 86400000).toISOString(),
      created_at: data.created_at || now,
      updated_at: now,
      synced: false,
    };
    await putItem('submissions', sub);
    if (sub.client_name) {
      const existing = clients.find(
        (c) => c.name?.toLowerCase() === sub.client_name.toLowerCase()
      );
      if (!existing) {
        const client = {
          id: crypto.randomUUID(),
          user_id: effectiveOwnerId || user.id,
          name: sub.client_name,
          address: sub.client_address,
          phone: sub.client_phone,
          email: sub.client_email,
          created_at: now,
          updated_at: now,
          synced: false,
        };
        await putItem('clients', client);
      }
    }
    await refreshLocal();
    if (navigator.onLine) doSync();
    return sub;
  };

  const updateStatus = async (id, status) => {
    const item = await getItem('submissions', id);
    if (!item) return;
    const updated = { ...item, status, updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const removeSubmission = async (id) => {
    const item = await getItem('submissions', id);
    if (!item) return;
    // mark as deleted for sync
    await putItem('submissions', { ...item, _deleted: true, updated_at: new Date().toISOString(), synced: false });
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const duplicateSubmission = async (id) => {
    const item = await getItem('submissions', id);
    if (!item) return null;
    const copy = { ...item };
    delete copy.id;
    delete copy._deleted;
    copy.submission_number = await getNextSubmissionNumber(effectiveOwnerId || user.id);
    copy.status = 'draft';
    copy.created_at = new Date().toISOString();
    copy.signed_at = null;
    copy.signed_by_name = null;
    copy.signature_data_url = null;
    copy.sign_token = null;
    copy.last_reminded_at = null;
    copy.client_notified_at = null;
    return saveSubmission(copy);
  };

  // ============ TEMPLATES (reusable quote blueprints) ============
  /**
   * Save the materials + labour structure of a submission (or any payload) as a reusable template.
   * Does NOT persist client info, signatures, photos or status.
   */
  const saveTemplate = async (data) => {
    if (!user) return null;
    const now = new Date().toISOString();
    const isNew = !data.id;
    const tpl = {
      id: data.id || crypto.randomUUID(),
      user_id: effectiveOwnerId || user.id,
      name: (data.name || '').trim() || 'Modèle sans nom',
      work_type: data.work_type || '',
      province: data.province || profile?.province || 'QC',
      materials: data.materials || [],
      labour: data.labour || {},
      margin: data.margin ?? profile?.default_margin ?? 15,
      miscFees: data.miscFees || 0,
      miscNotes: data.miscNotes || '',
      notes: data.notes || '',
      created_at: data.created_at || now,
      updated_at: now,
      synced: false,
    };
    await putItem('templates', tpl);
    await refreshLocal();
    if (navigator.onLine) doSync();
    return tpl;
  };

  const removeTemplate = async (id) => {
    const item = await getItem('templates', id);
    if (!item) return;
    await putItem('templates', { ...item, _deleted: true, updated_at: new Date().toISOString(), synced: false });
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  /**
   * Build a fresh submission DRAFT from a template (does NOT save it yet).
   * Caller (NewSubmission) merges this into form state.
   */
  const submissionFromTemplate = async (templateId) => {
    const tpl = await getItem('templates', templateId);
    if (!tpl) return null;
    // Re-generate fresh material IDs to avoid collisions if user adds multiple from same template
    const materials = (tpl.materials || []).map((m) => ({ ...m, id: crypto.randomUUID() }));
    return {
      work_type: tpl.work_type || '',
      province: tpl.province || profile?.province || 'QC',
      materials,
      labour: tpl.labour || {},
      margin: tpl.margin ?? profile?.default_margin ?? 15,
      miscFees: tpl.miscFees || 0,
      miscNotes: tpl.miscNotes || '',
      notes: tpl.notes || '',
    };
  };

  const saveProfile = async (patch) => {
    // 🔒 Team members cannot modify the owner's company profile.
    if (teamRole === 'member') {
      throw new Error(
        "Tu es membre d'une équipe. Seul le propriétaire de l'abonnement peut modifier le profil de l'entreprise."
      );
    }
    const now = new Date().toISOString();
    // First-time profile creation? If so, honor the province picked in onboarding.
    const isFirstSave = !profile;
    const hint = isFirstSave ? getOnboardingProvinceHint() : null;
    const merged = {
      user_id: user.id,
      email: user.email,
      language: 'fr',
      province: hint || 'QC',
      default_hourly_rate: 65,
      default_margin: 15,
      ...(profile || {}),
      ...patch,
      updated_at: now,
      synced: false,
    };
    await putItem('profiles', merged);
    setProfile(merged);
    if (navigator.onLine) {
      try {
        const { supabase } = await import('../lib/supabaseClient');
        const { synced, ...payload } = merged;
        // Strip null/undefined logo_url so older schemas without the column don't 400
        if (payload.logo_url == null) delete payload.logo_url;
        const { error } = await supabase
          .from('users_profiles')
          .upsert(payload, { onConflict: 'user_id' });
        if (error) {
          // If logo_url / tax ID columns missing, retry without optional columns
          if (/logo_url|business_number|gst_hst_number|qst_number/i.test(error.message)) {
            const {
              logo_url,
              business_number,
              gst_hst_number,
              qst_number,
              ...retry
            } = payload;
            const { error: e2 } = await supabase
              .from('users_profiles')
              .upsert(retry, { onConflict: 'user_id' });
            if (e2) throw e2;
          } else {
            throw error;
          }
        }
        await putItem('profiles', { ...merged, synced: true });
        // Consumed the onboarding hint (if any) on a successful cloud save.
        if (isFirstSave && hint) clearOnboardingProvinceHint();
      } catch (e) {
        console.error('saveProfile cloud error', e);
        // Keep local copy with synced=false; will retry on next sync
        throw e;
      }
    }
  };

  const uploadLogo = async (file) => {
    if (!user || !file) throw new Error('Not authenticated');
    if (teamRole === 'member') {
      throw new Error(
        "Tu es membre d'une équipe. Seul le propriétaire peut modifier le logo de l'entreprise."
      );
    }
    const { supabase } = await import('../lib/supabaseClient');
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `${user.id}/logo-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('logos')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) {
      // Helpful message when bucket doesn't exist
      if (/bucket.*not.*found|not_found|404/i.test(upErr.message)) {
        throw new Error('Bucket "logos" introuvable. Migration SQL à exécuter.');
      }
      throw upErr;
    }
    const { data } = supabase.storage.from('logos').getPublicUrl(path);
    const url = data.publicUrl;
    await saveProfile({ logo_url: url });
    return url;
  };

  const removeLogo = async () => {
    if (teamRole === 'member') {
      throw new Error(
        "Tu es membre d'une équipe. Seul le propriétaire peut modifier le logo de l'entreprise."
      );
    }
    await saveProfile({ logo_url: null });
  };

  const deleteClient = async (id) => {
    const item = await getItem('clients', id);
    if (!item) return;
    await putItem('clients', { ...item, _deleted: true, updated_at: new Date().toISOString(), synced: false });
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  // Generate a sign token if missing, return updated submission
  const ensureSignToken = async (id) => {
    const item = await getItem('submissions', id);
    if (!item) return null;
    if (item.sign_token) return item;
    const updated = {
      ...item,
      sign_token: crypto.randomUUID(),
      updated_at: new Date().toISOString(),
      synced: false,
    };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) await doSync();
    return updated;
  };

  // Save signature (data URL PNG) to the submission and mark accepted
  const saveSignature = async (id, signatureDataUrl, signedByName) => {
    const item = await getItem('submissions', id);
    if (!item) return null;
    const updated = {
      ...item,
      signature_data_url: signatureDataUrl,
      signed_by_name: signedByName || item.client_name || '',
      signed_at: new Date().toISOString(),
      status: 'accepted',
      updated_at: new Date().toISOString(),
      synced: false,
    };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) await doSync();
    return updated;
  };

  // Add a photo to a submission (uploads to Supabase Storage; stores public URL)
  const addPhotoToSubmission = async (submissionId, file, category = 'before') => {
    if (!user || !file) throw new Error('Not authenticated');
    const item = await getItem('submissions', submissionId);
    if (!item) throw new Error('Submission not found');
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${user.id}/${submissionId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('project-photos')
      .upload(path, file, { upsert: false, contentType: file.type });
    if (upErr) {
      if (/bucket.*not.*found|not_found|404/i.test(upErr.message)) {
        throw new Error('Bucket "project-photos" introuvable. Exécutez la migration SQL.');
      }
      throw upErr;
    }
    const { data } = supabase.storage.from('project-photos').getPublicUrl(path);
    const photo = {
      id: crypto.randomUUID(),
      url: data.publicUrl,
      path,
      category, // 'before' | 'after' | 'measurement' | 'defect'
      caption: '',
      uploaded_at: new Date().toISOString(),
    };
    const photos = [...(item.photos || []), photo];
    const updated = { ...item, photos, updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
    return photo;
  };

  const removePhotoFromSubmission = async (submissionId, photoId) => {
    const item = await getItem('submissions', submissionId);
    if (!item) return;
    const photo = (item.photos || []).find((p) => p.id === photoId);
    if (photo?.path) {
      try {
        await supabase.storage.from('project-photos').remove([photo.path]);
      } catch (err) {
        // Storage delete failed — likely already gone or offline. Local copy is still cleaned below.
        console.warn('Photo storage delete failed (non-blocking):', err);
      }
    }
    const photos = (item.photos || []).filter((p) => p.id !== photoId);
    const updated = { ...item, photos, updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const updatePhotoCaption = async (submissionId, photoId, patch) => {
    const item = await getItem('submissions', submissionId);
    if (!item) return;
    const photos = (item.photos || []).map((p) => (p.id === photoId ? { ...p, ...patch } : p));
    const updated = { ...item, photos, updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const saveExpenses = async (submissionId, expenses) => {
    const item = await getItem('submissions', submissionId);
    if (!item) return;
    const updated = { ...item, expenses: expenses || [], updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const markReminded = async (submissionId) => {
    const item = await getItem('submissions', submissionId);
    if (!item) return;
    const updated = { ...item, last_reminded_at: new Date().toISOString(), updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const markClientNotified = async (submissionId) => {
    const item = await getItem('submissions', submissionId);
    if (!item) return;
    const updated = { ...item, client_notified_at: new Date().toISOString(), updated_at: new Date().toISOString(), synced: false };
    await putItem('submissions', updated);
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const findInvoiceForSubmission = (submissionId) =>
    invoices.find((i) => i.source_submission_id === submissionId) || null;

  const saveInvoice = async (data) => {
    if (!user) return null;
    const now = new Date().toISOString();
    const ownerId = effectiveOwnerId || user.id;
    const inv = {
      id: data.id || crypto.randomUUID(),
      user_id: ownerId,
      invoice_number: data.invoice_number,
      source_submission_id: data.source_submission_id || null,
      source_submission_number: data.source_submission_number || null,
      status: data.status || 'draft',
      invoice_date: data.invoice_date,
      due_date: data.due_date,
      client_name: data.client_name || '',
      client_address: data.client_address || '',
      client_phone: data.client_phone || '',
      client_email: data.client_email || '',
      site_address: data.site_address || '',
      work_type: data.work_type || '',
      province: data.province || profile?.province || 'QC',
      materials: data.materials || [],
      labour: data.labour || {},
      summary: data.summary || {},
      notes: data.notes || '',
      conditions: data.conditions || '',
      created_at: data.created_at || now,
      updated_at: now,
      synced: false,
    };
    await putItem('invoices', inv);
    await refreshLocal();
    if (navigator.onLine) doSync();
    return inv;
  };

  const createInvoiceFromSubmission = async (submissionId) => {
    const sub = await getItem('submissions', submissionId);
    if (!sub) return { ok: false, error: 'missing_submission' };
    const existing = findInvoiceForSubmission(submissionId);
    const result = await convertSubmissionToInvoice(sub, {
      userId: effectiveOwnerId || user?.id,
      existingForSubmission: existing,
    });
    if (!result.ok) return result;
    const saved = await saveInvoice(result.invoice);
    return { ok: true, invoice: saved };
  };

  const removeInvoice = async (id) => {
    const item = await getItem('invoices', id);
    if (!item) return;
    await putItem('invoices', { ...item, _deleted: true, updated_at: new Date().toISOString(), synced: false });
    await refreshLocal();
    if (navigator.onLine) doSync();
  };

  const unsyncedCount = submissions.filter(s => s.synced === false).length +
    clients.filter(c => c.synced === false).length +
    templates.filter(t => t.synced === false).length +
    invoices.filter(i => i.synced === false).length;

  const value = useMemo(
    () => ({
      submissions,
      clients,
      templates,
      invoices,
      profile,
      online,
      syncing,
      loading,
      unsyncedCount,
      teamRole,
      ownerCompany,
      ownerEmail,
      isMember: teamRole === 'member',
      saveSubmission,
      updateStatus,
      removeSubmission,
      duplicateSubmission,
      saveTemplate,
      removeTemplate,
      submissionFromTemplate,
      getTemplate: (id) => getItem('templates', id),
      saveProfile,
      uploadLogo,
      removeLogo,
      deleteClient,
      ensureSignToken,
      saveSignature,
      addPhotoToSubmission,
      removePhotoFromSubmission,
      updatePhotoCaption,
      saveExpenses,
      markReminded,
      markClientNotified,
      getSubmission: (id) => getItem('submissions', id),
      saveInvoice,
      createInvoiceFromSubmission,
      findInvoiceForSubmission,
      removeInvoice,
      getInvoice: (id) => getItem('invoices', id),
      sync: doSync,
    }),
    // Only re-build when the actual reactive data changes. Methods are stable closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submissions, clients, templates, invoices, profile, online, syncing, loading, unsyncedCount, teamRole, ownerCompany, ownerEmail]
  );

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
